import { useMemo, useState, useEffect } from "react";
import { Plus, Eye, FileDown, MessageCircle, CheckSquare, Square, RefreshCcw, Download, Archive, ArchiveRestore, XCircle, Trash2 } from "lucide-react";
import { Page, Panel, DeleteButton, StatusPill, SearchInput, ExportMenu, FileSpreadsheet, usePagination, Pagination, useSort, SortTh, TotalsBar } from "../lib/ui";
import { money, can, humanDate, download, api, isUnpaidProforma, sendProformaWhatsApp, sumByCur, rowLink } from "../lib/api";

export default function Invoices({ rows, setModal, onDelete, user, onView, onReload, scope, setScope }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    setSelected((sel) => new Set([...sel].filter((id) => rows.some((r) => r.id === id))));
  }, [rows]);

  useEffect(() => { setSelected(new Set()); }, [scope]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const needle = q.toLowerCase();
    return rows.filter((i) => {
      if ((i.invoice_no || "").toLowerCase().includes(needle)) return true;
      if ((i.invoice_type || "").toLowerCase().includes(needle)) return true;
      if ((i.customer_name || "").toLowerCase().includes(needle)) return true;
      if ((i.chassis_no || "").toLowerCase().includes(needle)) return true;
      if ((i.plate || "").toLowerCase().includes(needle)) return true;
      if ((i.vehicles || []).some((v) => `${v.plate || ""} ${v.chassis_no || ""}`.toLowerCase().includes(needle))) return true;
      for (const p of i.products || []) {
        if ((p.description || "").toLowerCase().includes(needle)) return true;
        if ((p.extra_note || "").toLowerCase().includes(needle)) return true;
        if ((p.name || "").toLowerCase().includes(needle)) return true;
        if ((p.code || "").toLowerCase().includes(needle)) return true;
        if ((p.serial_no || "").toLowerCase().includes(needle)) return true;
      }
      for (const s of i.services || []) {
        if ((s.name || "").toLowerCase().includes(needle)) return true;
        if ((s.description || "").toLowerCase().includes(needle)) return true;
        if ((s.extra_note || "").toLowerCase().includes(needle)) return true;
        if ((s.serial_no || "").toLowerCase().includes(needle)) return true;
      }
      return false;
    });
  }, [rows, q]);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allChecked = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allChecked) filtered.forEach((r) => n.delete(r.id));
    else filtered.forEach((r) => n.add(r.id));
    return n;
  });
  const srt = useSort(filtered, {
    no: (r) => r.invoice_no, type: (r) => r.invoice_type || "Resmi", customer: (r) => r.customer_name, plate: (r) => r.plate || "",
    date: (r) => r.date, due: (r) => r.due_date || "", status: (r) => r.status, total: (r) => Number(r.total || 0),
  });
  const pg = usePagination(srt.sorted);

  const bulkExport = async (fmt) => {
    const res = await api.post("/invoices/export/bulk", { ids: [...selected], fmt }, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `faturalar-secim.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 400);
  };

  const bulkAction = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.post("/invoices/bulk-action", { ids: [...selected], action });
      setSelected(new Set());
      onReload?.();
    } catch (err) {
      alert(err.response?.data?.detail || "İşlem başarısız");
    }
  };

  const scopes = [
    ["active", "Aktif"],
    ["archived", "Arşiv"],
    ["cancelled", "İptal"],
    ["all", "Tümü"],
  ];

  return (
    <>
      <Page
        eyebrow="SATIŞ · FATURA"
        title="Faturalar"
        subtitle="Ürün ve hizmetlerinizi cari kartlarla eşleştirerek fatura oluşturun."
        action={
          <div className="button-row">
            <ExportMenu testid="export-invoices" options={[
              { key: "xlsx", label: "Excel (.xlsx)", icon: <FileSpreadsheet size={14} />, onClick: () => download("/invoices/export?fmt=xlsx", "faturalar.xlsx") },
              { key: "csv", label: "CSV (.csv)", icon: <FileSpreadsheet size={14} />, onClick: () => download("/invoices/export?fmt=csv", "faturalar.csv") },
            ]} />
            <button data-testid="add-invoices-button" className="primary-button" disabled={!can(user, "create_invoice")}
                    title={!can(user, "create_invoice") ? "Bu işlem için yetkiniz yok" : ""}
                    onClick={() => setModal({ type: "invoice" })}>
              <Plus size={17} /> Yeni fatura
            </button>
          </div>
        }
      />

      <div className="scope-tabs" data-testid="invoice-scope-tabs">
        {scopes.map(([id, label]) => (
          <button key={id} data-testid={`scope-${id}`}
                  className={scope === id ? "active" : ""}
                  onClick={() => setScope(id)}>{label}</button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="bulk-bar" data-testid="bulk-toolbar">
          <span><strong data-testid="bulk-selected-count">{selected.size}</strong> fatura seçildi</span>
          <div className="bulk-actions">
            {scope !== "cancelled" && (
              <button data-testid="bulk-status-button" className="secondary-button"
                      onClick={() => setModal({ type: "bulk-status", ids: [...selected] })}>
                <RefreshCcw size={14} /> Durum
              </button>
            )}
            <button data-testid="bulk-export-xlsx" className="secondary-button" onClick={() => bulkExport("xlsx")}>
              <Download size={14} /> Excel
            </button>
            <button data-testid="bulk-export-csv" className="secondary-button" onClick={() => bulkExport("csv")}>
              <Download size={14} /> CSV
            </button>
            {scope === "archived" ? (
              <button data-testid="bulk-unarchive" className="secondary-button"
                      onClick={() => bulkAction("unarchive")}>
                <ArchiveRestore size={14} /> Arşivden çıkar
              </button>
            ) : (
              <button data-testid="bulk-archive" className="secondary-button"
                      onClick={() => bulkAction("archive")}>
                <Archive size={14} /> Arşive taşı
              </button>
            )}
            {scope === "cancelled" ? (
              <button data-testid="bulk-uncancel" className="secondary-button"
                      onClick={() => bulkAction("uncancel")}>
                <RefreshCcw size={14} /> İptali geri al
              </button>
            ) : (
              <button data-testid="bulk-cancel" className="secondary-button danger-btn"
                      onClick={() => bulkAction("cancel", `${selected.size} fatura iptal edilecek. Bağlı tahsilatlar geri alınacak. Emin misiniz?`)}>
                <XCircle size={14} /> İptal et
              </button>
            )}
            <button data-testid="bulk-delete" className="secondary-button danger-btn"
                    disabled={!can(user, "delete_invoice")}
                    onClick={() => bulkAction("delete", `${selected.size} fatura KALICI olarak silinecek. Emin misiniz?`)}>
              <Trash2 size={14} /> Kalıcı sil
            </button>
            <button data-testid="bulk-clear" className="text-button" onClick={() => setSelected(new Set())}>
              Seçimi temizle
            </button>
          </div>
        </div>
      )}

      <Panel
        className="module-panel"
        action={<SearchInput value={q} onChange={setQ} testid="invoices-search" placeholder="Fatura no, cari, plaka, şase, ürün, seri no" />}
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <button data-testid="select-all-invoices" type="button" className="check-btn" onClick={toggleAll}>
                    {allChecked ? <CheckSquare size={15} /> : <Square size={15} />}
                  </button>
                </th>
                <SortTh label="Fatura no" k="no" s={srt} testid="invoices-sort-no" /><SortTh label="Tür" k="type" s={srt} testid="invoices-sort-type" />
                <SortTh label="Cari" k="customer" s={srt} testid="invoices-sort-customer" /><SortTh label="Plaka / Şase" k="plate" s={srt} testid="invoices-sort-plate" />
                <SortTh label="Tarih" k="date" s={srt} testid="invoices-sort-date" /><SortTh label="Vade" k="due" s={srt} testid="invoices-sort-due" />
                <SortTh label="Durum" k="status" s={srt} testid="invoices-sort-status" /><SortTh label="Toplam" k="total" s={srt} align="right" testid="invoices-sort-total" /><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="10" className="empty">Fatura bulunamadı.</td></tr>
              )}
              {pg.slice.map((r) => (
                <tr key={r.id} data-testid={`invoice-row-${r.id}`} className={selected.has(r.id) ? "row-selected" : (r.archived ? "row-archived" : "")}>
                  <td>
                    <button data-testid={`select-invoice-${r.id}`} type="button" className="check-btn" onClick={() => toggleOne(r.id)}>
                      {selected.has(r.id) ? <CheckSquare size={15} /> : <Square size={15} />}
                    </button>
                  </td>
                  <td style={{ cursor: "pointer" }}
                      {...rowLink(`/detay/fatura/${r.id}`)}>
                    <a href={`/detay/fatura/${r.id}`} className="row-anchor" onClick={(e) => e.stopPropagation()}><strong style={{ color: "#0e86b0", textDecoration: "underline dotted" }}>{r.invoice_no}</strong></a>
                    {r.archived && <small className="archived-tag">Arşiv</small>}
                  </td>
                  <td>
                    <span className={`type-tag ${r.invoice_type === "Proforma" ? "individual" : "corporate"}`}>
                      {r.invoice_type || "Resmi"}
                    </span>
                  </td>
                  <td>
                    {r.customer_id ? (
                      <button type="button" className="link-cell" data-testid={`invoice-customer-${r.id}`} title="Cari ekstresini aç"
                              onClick={() => setModal({ type: "statement", customerId: r.customer_id })}>{r.customer_name}</button>
                    ) : r.customer_name}
                  </td>
                  <td>
                    {r.plate ? <strong>{r.plate}</strong> : !r.chassis_no && <span>—</span>}
                    {r.chassis_no && <small>{r.chassis_no}</small>}
                    {(r.vehicles || []).length > 1 && <small data-testid={`invoice-more-vehicles-${r.id}`}>+{r.vehicles.length - 1} araç</small>}
                  </td>
                  <td>{humanDate(r.date)}</td>
                  <td>{humanDate(r.due_date)}</td>
                  <td><StatusPill status={r.status} /></td>
                  <td className="amount"><strong>{money(r.total)}</strong></td>
                  <td className="amount actions-cell">
                    <button data-testid={`view-invoice-${r.id}`} className="row-icon tone-muted" title="Görüntüle" onClick={() => onView(r)}>
                      <Eye size={15} />
                    </button>
                    <button data-testid={`pdf-invoice-${r.id}`} className="row-icon tone-muted" title="PDF indir"
                            onClick={() => download(`/invoices/${r.id}/pdf`, `fatura-${r.invoice_no}.pdf`)}>
                      <FileDown size={15} />
                    </button>
                    {isUnpaidProforma(r) && (
                      <button data-testid={`whatsapp-invoice-${r.id}`} className="row-icon tone-success" title="WhatsApp'tan gönder"
                              onClick={() => sendProformaWhatsApp(r).catch((e) => alert(e.response?.data?.detail || "WhatsApp linki oluşturulamadı"))}>
                        <MessageCircle size={15} />
                      </button>
                    )}
                    <DeleteButton
                      testid={`delete-invoice-${r.id}`}
                      disabled={!can(user, "delete_invoice")}
                      onClick={() => onDelete(r)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TotalsBar testid="invoices-totals" items={[
            { label: `${filtered.length} fatura`, value: "", testid: "invoices-totals-count" },
            { label: "Resmi", value: sumByCur(filtered.filter((r) => r.invoice_type !== "Proforma"), (r) => r.total), testid: "invoices-totals-resmi" },
            { label: "Proforma", value: sumByCur(filtered.filter((r) => r.invoice_type === "Proforma"), (r) => r.total), testid: "invoices-totals-proforma" },
            { label: "Bu sayfa", value: sumByCur(pg.slice, (r) => r.total), testid: "invoices-totals-page" },
            { label: "Genel toplam", value: sumByCur(filtered, (r) => r.total), tone: "grand", testid: "invoices-totals-grand" },
          ]} />
          <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                      onChange={pg.setPage} testid="invoices-pagination" />
        </div>
      </Panel>
    </>
  );
}
